/* eslint-env browser */
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

interface CollapseProps {
  children: React.ReactNode | (({ isAnimating }: { isAnimating: boolean }) => React.ReactNode);
  className?: string;
  isOpen: boolean;
  preservePreCloseState?: boolean;
}

export function Collapse({
  children,
  className,
  isOpen: isOpenInput,
  preservePreCloseState = false,
}: CollapseProps) {
  const [isOpenDelayed, setIsOpenDelayed] = useState(isOpenInput);
  const isOpen = preservePreCloseState ? isOpenInput : isOpenDelayed;
  const [isAnimating, setAnimating] = useState(false);

  useEffect(() => {
    // Allow components passed as children to re-render with accurate state
    // right before we trigger the close behavior. Without this workaround,
    // the animation library captures the component in the state just before
    // it is set to close. There are times where you don't want it to happen
    // though, like when swapping the entire contents of the Collapse children
    // when isOpen changes -- in that case, we'd like to avoid the flicker
    // of the content changing while the animation is happening. This is rare.
    const timer = setTimeout(() => {
      window.requestAnimationFrame(() => setIsOpenDelayed(isOpenInput));
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpenInput]);

  // The smoke tests throw an error when this animation component is used
  if (Collapse.disabled) {
    if (!isOpen) return null;
    return typeof children === 'function' ? <>{children({ isAnimating })}</> : <>{children}</>;
  }

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.section
          key="content"
          initial="collapsed"
          animate="open"
          exit="collapsed"
          variants={{
            open: { opacity: 1, height: 'auto' },
            collapsed: { opacity: 0, height: 0 },
          }}
          style={{ willChange: 'opacity, height', overflow: 'hidden' }}
          transition={{ duration: 0.125, ease: 'easeOut' }}
          onAnimationStart={() => setAnimating(true)}
          onAnimationComplete={() => setAnimating(false)}
          className={className}
        >
          {typeof children === 'function' ? <>{children({ isAnimating })}</> : children}
        </motion.section>
      )}
    </AnimatePresence>
  );
}

Collapse.disabled = false;
